const API_URL = process.env.REACT_APP_API_URL;

/**
 * Extract the best error message from a ProblemDetail response body, matching the
 * convention used across the other services (flowService, deliveryService et al.).
 */
function extractErrorMessage(errorBody, fallback) {
  if (!errorBody) return fallback;
  const detail = errorBody.detail;
  const title = errorBody.title;
  if (detail && title && title !== fallback) {
    return `${title}: ${detail}`;
  }
  return detail || fallback;
}

async function asJsonOrThrow(response, fallback) {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(error, fallback));
  }
  return await response.json();
}

/**
 * Client for the provider-agnostic connections API: the accounts an environment has authorised
 * with Google (and, once their connectors ship, Slack and Teams) so a destination can reuse one
 * instead of asking the merchant to sign in again per destination.
 */
class ConnectionsService {
  /** [{ id, environmentId, provider, accountLabel, scopes, status }] */
  async list(envId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/connections`
    );
    return asJsonOrThrow(response, 'Failed to load connections');
  }

  async delete(envId, connectionId, authenticatedFetch) {
    const response = await authenticatedFetch(
      `${API_URL}/api/environments/${envId}/connections/${connectionId}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(extractErrorMessage(error, 'Failed to disconnect'));
    }
  }

  /**
   * Absolute URL to start (or extend) a provider's consent flow, open it, don't fetch it.
   *
   * @param {string} provider e.g. "google"
   * @param {string} scope e.g. "sheets" or "calendar"; incremental, does not drop a scope
   *   already granted
   */
  connectUrl(provider, envId, scope) {
    const params = new URLSearchParams({ envId });
    if (scope) params.set('scope', scope);
    return `${API_URL}/api/integrations/${provider}/connect?${params.toString()}`;
  }
}

const connectionsService = new ConnectionsService();
export default connectionsService;
